import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, FolderX, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

const NON_LIVE = new Set(['Complete', 'Archive', 'Enquiry']);
const isLive = (p) => !NON_LIVE.has(p.status);
const companyOf = (p) => (String(p.project_ref || '').toUpperCase().startsWith('LC') ? 'CON' : 'DEV');

/**
 * Searchable job picker for a ledger line. The billed-to company's jobs float to
 * the top; the other company's still show below. Live jobs first, then completed /
 * archived. Extras at the foot: create a lightweight ad-hoc job, or file the line
 * to No project (ledger only). The user can assign ANY project by hand.
 */
export function ProjectPicker({
  value, onChange, projects, disabled = false, invalid = false, testid, onApplyAll,
  company = null, noProject = false, onNoProject, api, onAdhocCreated,
}) {
  const [open, setOpen] = useState(false);
  const [applyAll, setApplyAll] = useState(false);
  const [query, setQuery] = useState('');
  const [adhocCompany, setAdhocCompany] = useState(null);
  const [adhocName, setAdhocName] = useState('');
  const [creating, setCreating] = useState(false);
  const selected = projects.find(p => p.id === value);
  // Offer ad-hoc for both companies — the invoice's billed-to first. Creating one
  // for the OTHER company auto-flags the line to rebill (line company != billed-to).
  const adhocChoices = company === 'CON' ? ['CON', 'DEV'] : ['DEV', 'CON'];

  const { live, other } = useMemo(() => {
    const hay = (p) => `${p.project_ref || ''} ${p.client_name || ''} ${p.address_line1 || ''} ${p.town_city || ''} ${p.postcode || ''}`.toLowerCase();
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const keep = tokens.length ? (p) => tokens.every(t => hay(p).includes(t)) : () => true;
    const liveList = projects.filter(p => isLive(p) && keep(p));
    // The billed-to company's jobs come first (stable order preserved within each group).
    if (company === 'DEV' || company === 'CON') {
      liveList.sort((a, b) => (companyOf(a) === company ? 0 : 1) - (companyOf(b) === company ? 0 : 1));
    }
    return { live: liveList, other: projects.filter(p => !isLive(p) && keep(p)) };
  }, [projects, query, company]);

  const pick = (p) => {
    if (applyAll && onApplyAll) onApplyAll(p.id); else onChange(p.id);
    setApplyAll(false); setOpen(false);
  };
  const chooseNoProject = () => { onNoProject?.(); setOpen(false); };
  const createAdhoc = async () => {
    const name = adhocName.trim();
    if (name.length < 2) { toast.error('Give the ad-hoc job a short name'); return; }
    setCreating(true);
    try {
      const r = await api.post('/cost-inbox/adhoc-project', { name, company: adhocCompany || company || 'DEV' });
      const proj = r.data.project;
      toast.success(`Ad-hoc job ${proj.project_ref} created`);
      onAdhocCreated?.(proj);
      onChange(proj.id);
      setAdhocCompany(null); setAdhocName(''); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create ad-hoc job'); }
    finally { setCreating(false); }
  };

  const sub = (p) => [p.client_name, p.town_city || p.address_line1].filter(Boolean).join(' · ');
  const renderItem = (p) => (
    <CommandItem
      key={p.id}
      value={`${p.project_ref} ${p.client_name || ''} ${p.address_line1 || ''} ${p.town_city || ''} ${p.postcode || ''}`}
      onSelect={() => pick(p)}
      className="text-white data-[selected=true]:bg-white/10"
      data-testid={`project-option-${p.id}`}
    >
      <Check className={cn('mr-2 h-3.5 w-3.5 text-[#F5A623] shrink-0', value === p.id ? 'opacity-100' : 'opacity-0')} />
      <span className="flex flex-col min-w-0">
        <span className="font-medium truncate">{p.project_ref}{p.adhoc ? <span className="ml-1 text-[10px] text-sky-300">ad-hoc</span> : null}</span>
        {sub(p) && <span className="text-[11px] text-gray-400 truncate">{sub(p)}</span>}
      </span>
    </CommandItem>
  );

  const label = noProject ? 'No project' : (selected ? selected.project_ref : 'Project…');

  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQuery(''); setAdhocCompany(null); setAdhocName(''); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            'flex items-center justify-between w-full h-9 px-3 rounded-md text-sm bg-[#1E2530] border text-left disabled:opacity-60',
            invalid ? 'border-amber-500/50' : 'border-white/20',
            noProject ? 'text-amber-300' : 'text-white',
          )}
        >
          <span className={cn('truncate', !selected && !noProject && 'text-gray-500')}>{label}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0 bg-[#2A3240] border-white/10" align="start">
        <Command className="bg-transparent" shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search job, client or address…" className="text-white" data-testid="project-picker-search" />
          {onApplyAll && (
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-[#F5A623] border-b border-white/10 cursor-pointer select-none" data-testid={`${testid}-apply-all`}>
              <input type="checkbox" checked={applyAll} onChange={e => setApplyAll(e.target.checked)} className="accent-[#F5A623]" data-testid={`${testid}-apply-all-check`} />
              Apply my pick to all lines
            </label>
          )}
          <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain">
            {live.length === 0 && other.length === 0 && (
              <div className="py-6 text-center text-sm text-gray-400">No matching job.</div>
            )}
            {live.length > 0 && (
              <CommandGroup heading="Live jobs" className="[&_[cmdk-group-heading]]:text-gray-400">{live.map(renderItem)}</CommandGroup>
            )}
            {other.length > 0 && (
              <CommandGroup heading="Complete / archived" className="[&_[cmdk-group-heading]]:text-gray-500">{other.map(renderItem)}</CommandGroup>
            )}
            {(onNoProject || api) && (
              <CommandGroup heading="Not a listed job" className="[&_[cmdk-group-heading]]:text-gray-500 border-t border-white/10 mt-1 pt-1">
                {onNoProject && (
                  <CommandItem value="opt-no-project" onSelect={chooseNoProject} className="text-gray-200 data-[selected=true]:bg-white/10" data-testid={`${testid}-no-project`}>
                    <FolderX className="mr-2 h-3.5 w-3.5 text-amber-300 shrink-0" /> No project — add to ledger only
                    {noProject && <Check className="ml-auto h-3.5 w-3.5 text-amber-300" />}
                  </CommandItem>
                )}
                {api && adhocChoices.map(co => (
                  <CommandItem key={co} value={`opt-adhoc-${co}`} onSelect={() => setAdhocCompany(co)} className="text-sky-300 data-[selected=true]:bg-white/10" data-testid={`${testid}-adhoc-${co}`}>
                    <Plus className="mr-2 h-3.5 w-3.5 shrink-0" /> Add ad-hoc job · {co === 'CON' ? 'LUX Construction' : 'LUX Developments'}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {api && adhocCompany && (
            <div className="p-2 border-t border-white/10 space-y-1.5">
              <div className="text-[11px] text-sky-300 px-0.5">New ad-hoc job · {adhocCompany === 'CON' ? 'LUX Construction' : 'LUX Developments'}{company && adhocCompany !== company ? ' — will be flagged to rebill' : ''}</div>
              <div className="flex items-center gap-2">
                <Input autoFocus value={adhocName} onChange={e => setAdhocName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createAdhoc(); }}
                  placeholder="Ad-hoc job name…" className="h-8 bg-[#1E2530] border-white/20 text-white text-sm" data-testid="adhoc-name-input" />
                <Button size="sm" onClick={createAdhoc} disabled={creating} className="h-8 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] shrink-0" data-testid="adhoc-create-btn">
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </Button>
              </div>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
